import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenantContext } from 'src/context/useTenantContext';
import { orgRestaurantUpdate, orgRestaurants, tenantRestaurantCurrency } from 'src/services/apis/tenantRequests';
import { RestaurantUpdateDto } from 'src/services/apis/types';
import { QUERIES } from 'src/utils/react-query';

export const useRestaurants = () => {
  const { orgSlug } = useTenantContext();
  return useQuery({
    queryKey: [QUERIES.MY_RESTAURANTS, orgSlug],
    queryFn: () => orgRestaurants({ orgSlug }),
  });
};

export const useCurrentRestaurant = () => {
  const { restaurantId } = useTenantContext();
  const { data } = useRestaurants();
  if (!restaurantId) {
    return null;
  }
  return data?.data?.find((item) => item.id === restaurantId) ?? null;
};

// Member-readable (unlike useCurrentRestaurant, owner/admin only via useRestaurants) — this is
// what useMoney reads, so POS/Kitchen/Delivery/`me` callers see the real currency instead of a
// 403-driven VND fallback. staleTime keeps a device from riding a currency change made on
// another device for too long, and useUpdateRestaurantMutation invalidates it below on the rare
// occasion this device made the change itself.
export const useRestaurantCurrency = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  return useQuery({
    queryKey: [QUERIES.RESTAURANT_CURRENCY, orgSlug, restaurantId],
    enabled: Boolean(orgSlug && restaurantId),
    staleTime: 5 * 60 * 1000,
    queryFn: () => tenantRestaurantCurrency({ orgSlug, restaurantId }),
  });
};

export const useUpdateRestaurantMutation = () => {
  const queryClient = useQueryClient();
  const { orgSlug } = useTenantContext();
  return useMutation((payload: RestaurantUpdateDto) => orgRestaurantUpdate(payload.id, payload, { orgSlug }), {
    onSuccess: () => {
      return Promise.all([
        queryClient.refetchQueries([QUERIES.MY_RESTAURANTS, orgSlug]),
        // Prefix match (no restaurantId), not the current restaurant only: settings can edit a
        // restaurant other than the one being viewed, and that update must still invalidate it.
        queryClient.invalidateQueries([QUERIES.RESTAURANT_CURRENCY, orgSlug]),
      ]);
    },
  });
};
